import { supabase } from '../lib/supabase'
import type { WriteResult } from './projectWrites'

/**
 * THE TWO PIECES OF WRITING ON A CLIENT PAGE — 14 Oct 2026.
 *
 * **The outline** is what we are making. The approach, the shape of the
 * series, the thing that was pitched. Written once near the start and barely
 * touched afterwards. It exists because a project in pre-production had
 * nothing on its page to look at — Marjan's was a tracker and a foot — and a
 * schedule is not a reason to open a link.
 *
 * **The note** is what is happening right now. *"Episodes 1 and 2 are in.
 * Have a look at Richard's opening."* Changes most weeks, and it is the
 * cheapest thing on the page for stopping a client having to work out what
 * they are looking at.
 *
 * They are kept together because they are the same job — words in Portal's
 * own voice — and because the panel wants both in one read.
 *
 * Neither is ever invented on a client's behalf. Blank means the block is
 * not drawn, which is the honest answer and not a gap to fill with filler.
 */

export interface ClientWords {
  /** What is happening now. Shown under the project name. */
  note: string
  /** What we are making. Shown as its own block. */
  outline: string
}

export const NO_WORDS: ClientWords = { note: '', outline: '' }

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim()
}

export async function fetchClientWords(projectId: string): Promise<ClientWords> {
  if (!supabase || !projectId) return NO_WORDS

  const { data, error } = await supabase
    .from('projects')
    .select('client_note, outline')
    .eq('id', projectId)
    .maybeSingle()

  if (error) {
    console.warn(`[supabase] client words load failed: ${error.message}`)
    return NO_WORDS
  }
  const row = (data ?? {}) as Record<string, unknown>
  return { note: text(row.client_note), outline: text(row.outline) }
}

/**
 * Save one or both. An empty string is a real instruction — it clears the
 * field and the block stops being drawn — so it is written as null rather
 * than skipped. A key left out of `words` is not touched at all.
 */
export async function saveClientWords(
  projectId: string,
  words: Partial<ClientWords>,
): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'Supabase is not configured.' }

  const patch: Record<string, string | null> = {}
  if (words.note !== undefined) patch.client_note = words.note.trim() || null
  if (words.outline !== undefined) patch.outline = words.outline.trim() || null
  if (Object.keys(patch).length === 0) return { ok: true }

  const { error } = await supabase
    .from('projects')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', projectId)

  if (error) {
    const detail = `client words: ${error.message}${error.code ? ` (${error.code})` : ''}`
    console.error(`[supabase] write refused — ${detail}`)
    return { ok: false, error: detail }
  }
  return { ok: true }
}
